import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

import { graphql } from 'gatsby';

import { Cards, Image, Paragraph, TitleLink, TitleSecond, TitleThird } from 'common/components';
import { IArticle } from 'common/interfaces/article.interface';

import { ArticleSummary } from 'components/ArticleSummary/ArticleSummary';
import { AuthorReviewer } from 'components/AuthorReviewer/AuthorReviewer';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { CalloutStripe } from 'components/CalloutStripe/CalloutStripe';
import { CalloutStripeSmall } from 'components/CalloutStripe/CalloutStripeSmall';
import { ContainerBackground } from 'components/Containers/ContainerBackground';
import { ContainerDefault } from 'components/Containers/ContainerDefault';
import { Faq } from 'components/FAQ/FaqBlock';
import { IconTitleColumns } from 'components/IconTitleColumns/IconTitleColumns';
import { ImageContentGrid } from 'components/ImageContentGrid/ImageContentGrid';
import { LayoutCom as Layout } from 'components/LayoutCom/LayoutCom';
import { Lockup } from 'components/Lockup/Lockup';
import { MoreAbout } from 'components/Navigation/MoreAbout';
import { SEO } from 'components/SEO/SEO';
import { PageType } from 'components/SEO/enum';
import type { AlternateLink, Article } from 'components/SEO/types';
import { ColoredTitleScheme } from 'components/Schemes/SchemeRowBlocks/ColoredTitleScheme';
import { SchemeStepProgress } from 'components/Schemes/SchemeStepProgress/SchemeStepProgress';
import { SideShareMenu } from 'components/SideShareMenu/SideShareMenu';
import { Sidemenu } from 'components/Sidemenu/Sidemenu';
import { SubTextImage } from 'components/SubTextImage/SubTextImage';
import { TableTopBorderedTitles } from 'components/Tables/TableTopBorderedTitles';
import { RowLandingTabs } from 'components/Tabs/RowLandingTabs';
import { TitleCopyColumns } from 'components/TitleCopy/TitleCopyColumns';
import { TitleCopyRows } from 'components/TitleCopy/TitleCopyRows';
import { TopBorderedColumnsGrid } from 'components/TopBorderedColumnsGrid/TopBorderedColumnsGrid';
import { TwoColumnsGrid } from 'components/TwoColumnsGrid/TwoColumnsGrid';

import { ThemeEnum } from 'enum/ThemeEnum';
import { getBlockItemsById, getContentBlockById } from 'helpers/LandingContentHelper';

import Epic from 'icons/logos/colored/Epic.svg';
import Athena from 'icons/logos/colored/athena.svg';
import Microsoft from 'icons/logos/colored/microsoft-for-healthcare.svg';
import Nextgen from 'icons/logos/colored/nextgen.svg';
import Office from 'icons/logos/colored/office.svg';

import Directions from 'icons/directions.svg';
import Dollar from 'icons/dollar.svg';
import ListIcon from 'icons/report-list.svg';
import Ring from 'icons/ring.svg';
import Shield from 'icons/shield-tick.svg';

import { SecurityScheme, TimelineScheme } from './components';

import './pediatric-ehr.scss';

export const query = graphql`
  query PediatricEhrPageQuery($field_id: String!, $langcode: String!) {
    nodeArticle(field_id: { eq: $field_id }, langcode: { eq: $langcode }) {
      title
      field_short_title
      date: field_creation_date
      changed
      path {
        alias
      }
      field_id
      langcode
      field_meta_tags {
        ...MetaTagsAll
      }
      relationships {
        ...ArticlesRelContAll
        ...ArticleDesktopHeaderImage
        ...ArticleMobileHeaderImage
        ...ArticleBreadcrumbs
        field_cluster_menu {
          ... on Node {
            ...RelatedLanding
          }
        }
        field_content {
          ...ParagraphContentBlock1
          ...BlockCalloutStripe
          ...ParagraphArticleVideo
        }
      }
    }
    links: allNodeArticle(filter: { field_id: { eq: $field_id } }) {
      nodes {
        langcode
        path {
          alias
        }
      }
    }
    locales: allLocale {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

interface Props {
  data: {
    nodeArticle: IArticle;
    links: {
      nodes: AlternateLink[];
    };
  };
}

enum PediatricEhrBlockIds {
  PediatricIntro = 'pediatric_intro',
  PediatricLinks = 'pediatric_links',
  Features = 'features',
  CtaTurn = 'cta_turn',
  EhrIntegrations = 'ehr_integrations',
  EhrSolutions = 'ehr_solutions',
  CtaContactUs = 'cta_contact_us',
  EhrSoftware = 'ehr_software',
  Roadmap = 'roadmap',
  Benefits = 'benefits',
  Security = 'security',
  Issues = 'issues',
  Level = 'level',
  CtaContactUs2 = 'cta_contact_us2',
  FAQ = 'faq',
}

const SolutionsIcons = [<Microsoft />, <Epic />, <Athena />, <Nextgen />, <Office />];

const schemeData = {
  field_title: 'Pediatric EHR',
  field_list: [
    'Hospital management software',
    'Revenue cycle management software',
    'Healthcare CRM',
    'Telehealth',
    'Patient portal',
  ],
};

const iconsArray = [<ListIcon />, <Shield />, <Directions />, <Dollar />, <Ring />];

export default function PediatricEhr({ data }: Props) {
  const { title, date, relationships, field_meta_tags, changed, field_short_title, langcode } = data.nodeArticle;
  const {
    field_desktop_header_image,
    field_author: author,
    field_reviewer: reviewer,
    field_content,
    field_related_content,
    field_mobile_header_image,
    field_breadcrumbs,
    field_cluster_menu,
  } = relationships;
  const images = {
    desktop: field_desktop_header_image.localFile?.childImageSharp?.gatsbyImageData,
    mobile: field_mobile_header_image.localFile?.childImageSharp?.gatsbyImageData,
  };

  const { i18n } = useTranslation();

  if (i18n.language !== langcode) {
    i18n.changeLanguage(langcode);
  }

  const seoData: Article = {
    pageType: PageType.Article,
    path: data.nodeArticle.path.alias,
    title,
    image: field_desktop_header_image.localFile?.publicURL,
    author,
    metaDescription: field_meta_tags.description || '',
    breadcrumbs: field_breadcrumbs,
    followingAuthors: null,
    datePublished: date,
    dateModified: changed,
    articleCategory: relationships.field_category[0]?.name,
  };

  return (
    <Layout langcode={langcode} layoutClass="no-overflow" theme={ThemeEnum.Tertiary} alternateLinks={data.links.nodes}>
      <SEO<Article>
        data={seoData}
        metaTags={{ ...field_meta_tags, content_language: langcode }}
        alternateLinks={data.links.nodes}
      />

      <Lockup images={images} title={title} date={date} />

      <Breadcrumbs links={field_breadcrumbs} pageName={field_short_title!} />

      <div id="side-menu-scroll-container" className="pediatric-ehr">
        <ContainerDefault containerClass="has-side-menu breadcrumbs-margin pediatric-ehr-intro section-margin-bottom-default">
          <TwoColumnsGrid
            rightClass="one-column-menu-small"
            leftContent={
              <>
                <AuthorReviewer author={author} reviewer={reviewer} />
                <Paragraph
                  item={ReactHtmlParser(
                    getContentBlockById(PediatricEhrBlockIds.PediatricIntro, field_content).field_description.value,
                  )}
                  parseHtml
                />
                <ArticleSummary item={getBlockItemsById(PediatricEhrBlockIds.PediatricIntro, field_content)[0]} />
              </>
            }
            rightContent={
              <Sidemenu
                share={<SideShareMenu preText={title} containerClass="article-share-menu" />}
                items={getBlockItemsById(PediatricEhrBlockIds.PediatricLinks, field_content).map((item) => ({
                  ...item,
                  title: item.field_title,
                  link: item.field_id,
                }))}
                isLanding
                type="Article"
              />
            }
          />

          <SubTextImage
            items={getBlockItemsById(PediatricEhrBlockIds.PediatricIntro, field_content).slice(1, 4)}
            percents={['3.4', '90%', '49.65']}
            percentSupTexts={['h/day', '', 'billion']}
            subTextPosition={['bottom', '', 'bottom']}
          />
        </ContainerDefault>

        <ContainerBackground
          id={PediatricEhrBlockIds.Features}
          containerClass="background-light-gray has-cta-after pediatric-ehr-features"
        >
          <TwoColumnsGrid
            leftContent={<TitleCopyRows items={[getContentBlockById(PediatricEhrBlockIds.Features, field_content)]} />}
            rightContent={<TimelineScheme />}
          />

          <TitleThird title={getBlockItemsById(PediatricEhrBlockIds.Features, field_content)[0].field_title} />

          <RowLandingTabs
            multiRow
            tabsItem={getBlockItemsById(PediatricEhrBlockIds.Features, field_content)
              .slice(1)
              .map((item) => ({
                title: item.field_title,
                text: item.field_description.value,
              }))}
          />
        </ContainerBackground>

        <CalloutStripe item={getContentBlockById(PediatricEhrBlockIds.CtaTurn, field_content)} />

        <ContainerDefault id="integrations" containerClass="pediatric-ehr-integrations">
          <TitleCopyColumns
            items={[getContentBlockById(PediatricEhrBlockIds.EhrIntegrations, field_content)]}
            isBold={false}
            parseHtml
          />

          <ColoredTitleScheme items={schemeData} />

          <TopBorderedColumnsGrid
            noBorderTop
            columnsNumber={3}
            items={getBlockItemsById(PediatricEhrBlockIds.EhrIntegrations, field_content).map((item) => ({
              ...item,
              title: item.field_title,
              link: item.field_link || '',
              content: <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml />,
            }))}
            titleClass="fifth-level-title"
          />
        </ContainerDefault>

        <ContainerBackground
          id="platforms"
          containerClass="background-light-gray has-cta-after pediatric-ehr-solutions"
        >
          <TwoColumnsGrid
            containerClass="title-wrapper"
            rightContent={
              <div
                dangerouslySetInnerHTML={{
                  __html: getContentBlockById(PediatricEhrBlockIds.EhrSolutions, field_content).field_description.value,
                }}
              />
            }
            leftContent={
              <TitleSecond title={getContentBlockById(PediatricEhrBlockIds.EhrSolutions, field_content).field_title} />
            }
          />

          <RowLandingTabs
            tabsClass="pediatric-ehr-solutions-tabs"
            tabsItem={getBlockItemsById(PediatricEhrBlockIds.EhrSolutions, field_content)
              .slice(0, 6)
              .map((item, index) => ({
                tabNav: item.field_title,
                optionalContent: (
                  <TwoColumnsGrid
                    leftContent={
                      <>
                        {index === 0 && (
                          <div className="microsoft-container">
                            {SolutionsIcons[index]}
                            <TitleLink link={item.field_link} title="" />
                          </div>
                        )}
                        {index <= 4 && index !== 0 && SolutionsIcons[index]}
                        {index === 5 && <TitleThird title={item.field_title} />}
                        <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml />
                      </>
                    }
                    rightContent={
                      // Fragment is useful because index === 5 will not contain an image
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <>
                        {index <= 4 && index !== 0 && (
                          <Image
                            image={
                              getBlockItemsById(PediatricEhrBlockIds.EhrSolutions, field_content).slice(6)[index - 1]
                                .relationships.field_image?.localFile?.childImageSharp.gatsbyImageData
                            }
                            alt={
                              getBlockItemsById(PediatricEhrBlockIds.EhrSolutions, field_content).slice(6)[index - 1]
                                .field_image.alt
                            }
                          />
                        )}
                      </>
                    }
                  />
                ),
              }))}
          />
        </ContainerBackground>

        <CalloutStripe item={getContentBlockById(PediatricEhrBlockIds.CtaContactUs, field_content)} />

        <ContainerBackground
          id="tips"
          containerClass="background-light-gray has-no-margin-bottom pediatric-ehr-software"
        >
          <TitleCopyColumns
            items={[getContentBlockById(PediatricEhrBlockIds.EhrSoftware, field_content)]}
            isBold={false}
            parseHtml
          />

          <IconTitleColumns
            items={getBlockItemsById(PediatricEhrBlockIds.EhrSoftware, field_content).map((item, index) => ({
              title: item.field_title,
              text: item.field_description.value,
              icon: iconsArray[index],
            }))}
            iconTitleColumnsClass="columns-3"
          />
        </ContainerBackground>

        <ContainerBackground
          id={PediatricEhrBlockIds.Roadmap}
          containerClass="background-theme has-no-margin-bottom has-scheme-progress-outstanding section-padding-bottom-large"
        >
          <TitleCopyColumns
            isInOutstandingWrapper
            noPaddingTop
            items={[getContentBlockById(PediatricEhrBlockIds.Roadmap, field_content)]}
          />

          <SchemeStepProgress
            needWrap
            isOutstanding
            data={getBlockItemsById(PediatricEhrBlockIds.Roadmap, field_content).map((item, index) => ({
              field_title: item.field_title,
              field_number: index === 0 || index === 1 ? (index + 1).toString() : '' || (index === 5 ? '3' : ''),
              field_description: [item.field_description.value],
            }))}
          />
        </ContainerBackground>

        <ContainerDefault
          containerClass="pediatric-ehr-benefits section-background has-no-margin-bottom"
          id={PediatricEhrBlockIds.Benefits}
        >
          <TitleCopyColumns
            items={[getContentBlockById(PediatricEhrBlockIds.Benefits, field_content)]}
            isBold={false}
            parseHtml
          />

          <RowLandingTabs
            multiRow
            tabsItem={getBlockItemsById(PediatricEhrBlockIds.Benefits, field_content).map((item) => ({
              title: item.field_title,
              text: item.field_description.value,
            }))}
          />
        </ContainerDefault>

        <ContainerBackground
          id={PediatricEhrBlockIds.Security}
          containerClass="background-light-gray pediatric-ehr-security section-padding-top-large section-padding-bottom-large"
        >
          <TwoColumnsGrid
            leftClass="main-grid-less-quarter"
            rightClass="main-grid-bigger-quarter"
            rightContent={<SecurityScheme />}
            leftContent={<TitleCopyRows items={[getContentBlockById(PediatricEhrBlockIds.Security, field_content)]} />}
          />
        </ContainerBackground>

        <ContainerDefault id={PediatricEhrBlockIds.Issues} containerClass="pediatric-ehr-issues">
          <TitleCopyColumns
            items={[getContentBlockById(PediatricEhrBlockIds.Issues, field_content)]}
            isBold={false}
            parseHtml
          />

          <TableTopBorderedTitles
            isQuote
            items={getBlockItemsById(PediatricEhrBlockIds.Issues, field_content).map((item) => ({
              field_description: { value: item.field_title },
              field_list: item.field_list,
              field_title: item.field_description.value,
            }))}
          />
        </ContainerDefault>

        <ImageContentGrid
          title={getContentBlockById(PediatricEhrBlockIds.Level, field_content).field_title}
          backgroundClass="background-light-gray has-half-cta-after"
          imageName={getBlockItemsById(PediatricEhrBlockIds.Level, field_content)[0]}
          imageClass="clear-top-padding"
          content={
            <Paragraph item={getContentBlockById(PediatricEhrBlockIds.Level, field_content).field_description.value} />
          }
        />

        <ContainerDefault containerClass="half-callout-wrapper">
          <CalloutStripeSmall item={getContentBlockById(PediatricEhrBlockIds.CtaContactUs2, field_content)} />
        </ContainerDefault>

        <ContainerDefault id={PediatricEhrBlockIds.FAQ} containerClass="pediatric-ehr-faq">
          <Faq
            title={getContentBlockById(PediatricEhrBlockIds.FAQ, field_content)}
            items={getBlockItemsById(PediatricEhrBlockIds.FAQ, field_content).map((item, index) => ({
              title: item.field_title,
              content:
                index === 2 ? <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml /> : undefined,
              text: index !== 2 ? item.field_description.value : undefined,
            }))}
          />
        </ContainerDefault>
      </div>

      <Cards items={field_related_content} />

      <MoreAbout langcode={langcode} id={field_cluster_menu!.field_id!} relatedContent={field_related_content} />
    </Layout>
  );
}
